import { useState, useEffect, useCallback, useMemo } from "react";
import productsService from "../services/productsService";
import { STORAGE_KEYS } from "./useNavigationMemory";
// Configuración del caché
export const CACHE_CONFIG = {
  STORAGE_KEY: STORAGE_KEYS.PRODUCTS_STATE,
  METADATA_KEY: STORAGE_KEYS.CACHE_METADATA,
  // 🆕 NUEVO: Caché específico para productos destacados del home
  HOME_STORAGE_KEY: "tattersall-home-products-cache",
  HOME_METADATA_KEY: "tattersall-home-cache-metadata",
  VERSION: "3.1", // Incrementado para el nuevo sistema optimizado
  TTL: 30 * 60 * 1000, // 30 minutos en millisegundos
  // 🆕 NUEVO: TTL más corto para productos destacados (se actualizan más frecuentemente)
  HOME_TTL: 15 * 60 * 1000, // 15 minutos para productos destacados
  MIN_CACHE_SIZE: 0, // Mínimo para considerar válido
  INITIAL_PAGE_SIZE: 10, // Solo cargar 10 productos inicialmente
  // 🆕 NUEVO: Configuración específica para home
  HOME_FEATURED_SIZE: 6, // Cantidad de productos destacados para el home
  MAX_CACHE_PAGES: 20, // Máximo de páginas a mantener en caché
};

const useProductsCache = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [cacheSource, setCacheSource] = useState("none"); // 'sessionStorage', 'api', 'none'

  // Función para guardar productos en sessionStorage
  const saveToSessionStorage = useCallback((products) => {
    try {
      if (!products || products.length === 0) return;

      const cacheData = {
        products,
        timestamp: Date.now(),
        version: CACHE_CONFIG.VERSION,
        count: products.length,
      };

      const metadata = {
        lastUpdate: Date.now(),
        version: CACHE_CONFIG.VERSION,
        productCount: products.length,
        pages: Math.ceil(products.length / 50),
        source: "api",
      };

      sessionStorage.setItem(
        CACHE_CONFIG.STORAGE_KEY,
        JSON.stringify(cacheData)
      );
      sessionStorage.setItem(
        CACHE_CONFIG.METADATA_KEY,
        JSON.stringify(metadata)
      );

      console.log("💾 Products saved to sessionStorage:", {
        count: products.length,
        size: `${(JSON.stringify(cacheData).length / 1024).toFixed(2)}KB`,
      });
    } catch (error) {
      console.warn("⚠️ Failed to save products to sessionStorage:", error);
    }
  }, []);

  // Función para limpiar sessionStorage
  const clearSessionStorage = useCallback(() => {
    try {
      sessionStorage.removeItem(CACHE_CONFIG.STORAGE_KEY);
      sessionStorage.removeItem(CACHE_CONFIG.METADATA_KEY);
      console.log("🗑️ Products cache cleared from sessionStorage");
    } catch (err) {
      console.warn("⚠️ Failed to clear sessionStorage:", err);
    }
  }, []);

  // Función para cargar productos desde sessionStorage
  const loadFromSessionStorage = useCallback(() => {
    try {
      const cachedData = sessionStorage.getItem(CACHE_CONFIG.STORAGE_KEY);
      const metadata = sessionStorage.getItem(CACHE_CONFIG.METADATA_KEY);

      if (!cachedData || !metadata) {
        console.log("📦 No products cache found in sessionStorage");
        return null;
      }

      const parsedData = JSON.parse(cachedData);
      const parsedMetadata = JSON.parse(metadata);

      // Validar versión del caché
      if (parsedData.version !== CACHE_CONFIG.VERSION) {
        console.log("🔄 Cache version mismatch, clearing old cache");
        clearSessionStorage();
        return null;
      }

      // Validar TTL (tiempo de vida)
      const now = Date.now();
      const cacheAge = now - parsedData.timestamp;

      if (cacheAge > CACHE_CONFIG.TTL) {
        console.log("⏰ Cache expired, clearing old cache:", {
          age: `${Math.round(cacheAge / 1000 / 60)}min`,
          ttl: `${CACHE_CONFIG.TTL / 1000 / 60}min`,
        });
        clearSessionStorage();
        return null;
      }

      // Validar tamaño mínimo del caché
      if (
        !parsedData.products ||
        parsedData.products.length < CACHE_CONFIG.MIN_CACHE_SIZE
      ) {
        console.log(
          "📊 Cache too small, fetching fresh data:",
          parsedData.products?.length || 0
        );
        return null;
      }

      console.log("✅ Products loaded from sessionStorage:", {
        count: parsedData.products.length,
        age: `${Math.round(cacheAge / 1000 / 60)}min`,
        source: parsedMetadata.source,
      });

      return {
        products: parsedData.products,
        timestamp: parsedData.timestamp,
        metadata: parsedMetadata,
      };
    } catch (err) {
      console.warn("⚠️ Failed to load products from sessionStorage:", err);
      clearSessionStorage();
      return null;
    }
  }, [clearSessionStorage]);

  // Función para cargar productos desde la API - SOLO CARGA INICIAL MÍNIMA
  const fetchInitialProductsFromAPI = useCallback(async () => {
    console.log("🌐 Fetching initial products from API (minimal load)...");

    try {
      // Solo cargar 1 página inicial de 10 productos para opciones de filtros
      const response = await productsService.getProducts({
        page: 1,
        limit: CACHE_CONFIG.INITIAL_PAGE_SIZE,
        featured: false,
        area: 1, // ✅ NUEVO: Filtrar por área "Bienes" por defecto
      });

      if (response && response.products) {
        console.log("✅ Initial API fetch completed:", {
          products: response.products.length,
          total: response.pagination?.total || 0,
        });

        return response.products;
      } else {
        throw new Error("No products received from API");
      }
    } catch (error) {
      console.error("❌ Error fetching initial products from API:", error);
      throw error;
    }
  }, []);

  // Cargar productos iniciales (desde caché o API)
  const loadInitialProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Intentar cargar desde sessionStorage primero
      const cachedData = loadFromSessionStorage();

      if (cachedData && cachedData.products?.length > 0) {
        setAllProducts(cachedData.products);
        setLastFetch(cachedData.timestamp);
        setCacheSource("sessionStorage");
        setLoading(false);

        console.log(
          "🚀 Cache hit! Products loaded instantly from sessionStorage"
        );
        return;
      }

      // Si no hay caché válido, cargar desde API
      setCacheSource("api");
      const freshProducts = await fetchInitialProductsFromAPI();

      if (freshProducts.length > 0) {
        setAllProducts(freshProducts);
        setLastFetch(Date.now());

        // Guardar en sessionStorage para futuras cargas
        saveToSessionStorage(freshProducts);

        console.log("✅ Fresh products loaded from API and cached");
      } else {
        throw new Error("No products received from API");
      }
    } catch (err) {
      console.error("❌ Error loading products:", err);
      setError(err.message);
      setCacheSource("none");
    } finally {
      setLoading(false);
    }
  }, [
    loadFromSessionStorage,
    fetchInitialProductsFromAPI,
    saveToSessionStorage,
  ]);

  // Extraer opciones únicas para filtros desde el cache
  const filterOptions = useMemo(() => {
    if (!allProducts.length) {
      return {
        categories: [],
        brands: [],
        locations: [],
        features: [],
        priceRange: { min: 0, max: 0 },
        yearRange: { min: 2015, max: 2025 },
      };
    }

    const categories = [...new Set(allProducts.map((p) => p.category))].filter(
      Boolean
    );

    const brands = [
      ...new Set(
        allProducts.map((p) => {
          const firstWord = p.model?.split(" ")[0];
          return firstWord;
        })
      ),
    ].filter(Boolean);

    const locations = [...new Set(allProducts.map((p) => p.ubicacion))].filter(
      Boolean
    );

    const features = [
      ...new Set(allProducts.flatMap((p) => p.features || [])),
    ].filter(Boolean);

    // Extraer rangos de precios reales
    const prices = allProducts
      .map((p) => {
        const priceStr = p.price?.toString().replace(/[^\d]/g, "") || "0";
        return parseInt(priceStr) || 0;
      })
      .filter((p) => p > 0);

    const years = allProducts.map((p) => p.year).filter(Boolean);

    return {
      categories: categories.sort(),
      brands: brands.sort(),
      locations: locations.sort(),
      features: features.sort(),
      priceRange: {
        min: prices.length ? Math.min(...prices) : 0,
        max: prices.length ? Math.max(...prices) : 500000000,
      },
      yearRange: {
        min: years.length ? Math.min(...years) : 2015,
        max: years.length ? Math.max(...years) : 2025,
      },
    };
  }, [allProducts]);

  // Filtrar productos localmente
  const filterProducts = useCallback(
    (filters) => {
      return allProducts.filter((product) => {
        // Filtro de búsqueda
        if (filters.search && filters.search.trim()) {
          const searchTerm = filters.search.toLowerCase().trim();
          const searchableText = `${product.name} ${product.model} ${
            product.category
          } ${product.description || ""}`.toLowerCase();
          if (!searchableText.includes(searchTerm)) return false;
        }

        // Filtro de categoría
        if (
          filters.category &&
          filters.category !== "Todas" &&
          product.category !== filters.category
        ) {
          return false;
        }

        // Filtro de marca
        if (filters.modelo && filters.modelo !== "Todas") {
          const productBrand = product.model?.split(" ")[0];
          if (productBrand !== filters.modelo) return false;
        }

        // Filtro de precio
        if (filters.priceRange && Array.isArray(filters.priceRange)) {
          const productPrice = parseInt(
            product.price?.replace(/[^\d]/g, "") || "0"
          );
          if (
            productPrice < filters.priceRange[0] ||
            productPrice > filters.priceRange[1]
          ) {
            return false;
          }
        }

        // Filtro de año
        if (
          filters.yearRange &&
          Array.isArray(filters.yearRange) &&
          product.year
        ) {
          if (
            product.year < filters.yearRange[0] ||
            product.year > filters.yearRange[1]
          ) {
            return false;
          }
        }

        // Filtro de condición
        if (
          filters.condition &&
          filters.condition !== "Todas" &&
          product.condition !== filters.condition
        ) {
          return false;
        }

        // Filtro de ubicación
        if (
          filters.ubicacion &&
          filters.ubicacion !== "Todas" &&
          product.ubicacion !== filters.ubicacion
        ) {
          return false;
        }

        // Filtro de características
        if (
          filters.features &&
          Array.isArray(filters.features) &&
          filters.features.length > 0
        ) {
          const productFeatures = product.features || [];
          const hasAllFeatures = filters.features.every((feature) =>
            productFeatures.includes(feature)
          );
          if (!hasAllFeatures) return false;
        }

        return true;
      });
    },
    [allProducts]
  );

  // Función para refrescar cache (forzar recarga desde API)
  const refreshCache = useCallback(async () => {
    console.log("🔄 Force refreshing cache...");
    clearSessionStorage();
    setCacheSource("none");
    await loadInitialProducts();
  }, [clearSessionStorage, loadInitialProducts]);

  // Función para obtener información del caché
  const getCacheInfo = useCallback(() => {
    try {
      const cachedData = sessionStorage.getItem(CACHE_CONFIG.STORAGE_KEY);
      const metadata = sessionStorage.getItem(CACHE_CONFIG.METADATA_KEY);

      if (!cachedData || !metadata) {
        return null;
      }

      const parsedData = JSON.parse(cachedData);
      const parsedMetadata = JSON.parse(metadata);
      const now = Date.now();
      const age = now - parsedData.timestamp;

      return {
        hasCache: true,
        productCount: parsedData.products?.length || 0,
        ageMinutes: Math.round(age / 1000 / 60),
        sizeKB: Math.round(cachedData.length / 1024),
        version: parsedData.version,
        source: parsedMetadata.source,
        isExpired: age > CACHE_CONFIG.TTL,
        ttlMinutes: Math.round(CACHE_CONFIG.TTL / 1000 / 60),
      };
    } catch (err) {
      console.warn("Error getting cache info:", err);
      return null;
    }
  }, []);

  // Cargar datos iniciales al montar el componente
  useEffect(() => {
    loadInitialProducts();
  }, [loadInitialProducts]);

  return {
    allProducts,
    loading,
    error,
    filterOptions,
    filterProducts,
    refreshCache,
    cacheSize: allProducts.length,
    lastFetch,
    cacheSource,
    getCacheInfo,
    clearCache: clearSessionStorage,
    // Información adicional para debugging
    cacheStats: {
      source: cacheSource,
      productCount: allProducts.length,
      lastFetch: lastFetch ? new Date(lastFetch).toLocaleString() : null,
      hasSessionStorage: !!sessionStorage.getItem(CACHE_CONFIG.STORAGE_KEY),
    },
  };
};

export default useProductsCache;
